import { useState, useCallback, useEffect, useRef } from "react";
import type { INavigationNodeDto } from "@/types/filteredContentTypes";
import { apiService } from "@/services/apiService";
import { authService } from "@/services/authService";
import { getPath, normalizePathForCompare } from "./navigationPathUtils";

interface INavigationState {
  rootNodes: INavigationNodeDto[];
  expandedItems: Set<string>;
  loadedChildren: Map<string, INavigationNodeDto[]>;
  loadingChildren: Set<string>;
  isLoading: boolean;
  error: string | null;
  searchResults: INavigationNodeDto[];
  isSearching: boolean;
}

interface IUseNavigationReturn extends INavigationState {
  handleToggle: (nodeKey: string) => void;
  handleLoadChildren: (nodeKey: string) => Promise<void>;
  handleSearch: (searchTerm: string) => Promise<void>;
  clearSearch: () => void;
  findPageTitle: (targetPath: string) => string;
  ensureExpandedToPath: (targetPath: string) => Promise<void>;
}

export function useNavigation(locale: string): IUseNavigationReturn {
  const [rootNodes, setRootNodes] = useState<INavigationNodeDto[]>([]);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [loadedChildren, setLoadedChildren] = useState<Map<string, INavigationNodeDto[]>>(
    new Map()
  );
  const [loadingChildren, setLoadingChildren] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchResults, setSearchResults] = useState<INavigationNodeDto[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  const inFlightChildrenFetchesRef = useRef<Map<string, Promise<INavigationNodeDto[]>>>(new Map());
  const lastAutoExpandedPathRef = useRef<string | null>(null);
  const expansionGenerationRef = useRef(0);
  const searchAbortControllerRef = useRef<AbortController | null>(null);

  const getNormalizedPathFromUrl = useCallback(
    (url: string | null): string => normalizePathForCompare(getPath(url, locale)),
    [locale]
  );

  // Load root nodes on mount
  useEffect(() => {
    async function loadRootNodes(): Promise<void> {
      try {
        setIsLoading(true);
        await authService.createAuthProvider();
        const nodes = await apiService.getNodes(locale);
        setRootNodes(nodes);
        setError(null);
      } catch (err) {
        console.error("Failed to load navigation:", err);
        setError("Failed to load navigation");
      } finally {
        setIsLoading(false);
      }
    }

    void loadRootNodes();
  }, [locale]);

  useEffect(() => {
    lastAutoExpandedPathRef.current = null;
    inFlightChildrenFetchesRef.current.clear();
  }, [locale]);

  // Toggle expand/collapse
  const handleToggle = useCallback((nodeKey: string): void => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(nodeKey)) {
        next.delete(nodeKey);
      } else {
        next.add(nodeKey);
      }
      return next;
    });
  }, []);

  // Load children for a node (lazy loading)
  const handleLoadChildren = useCallback(
    async (nodeKey: string): Promise<void> => {
      if (loadedChildren.has(nodeKey) || loadingChildren.has(nodeKey)) {
        return;
      }

      setLoadingChildren((prev) => new Set(prev).add(nodeKey));

      try {
        const children = await apiService.getNodeChildren(nodeKey, locale);
        setLoadedChildren((prev) => new Map(prev).set(nodeKey, children));
      } catch (err) {
        console.error(`Failed to load children for node ${nodeKey}:`, err);
      } finally {
        setLoadingChildren((prev) => {
          const next = new Set(prev);
          next.delete(nodeKey);
          return next;
        });
      }
    },
    [locale, loadedChildren, loadingChildren]
  );

  const getOrFetchChildren = useCallback(
    async (nodeKey: string): Promise<INavigationNodeDto[]> => {
      const existing = loadedChildren.get(nodeKey);
      if (existing) {
        return existing;
      }

      const inFlight = inFlightChildrenFetchesRef.current.get(nodeKey);
      if (inFlight) {
        return inFlight;
      }

      const fetchPromise = (async (): Promise<INavigationNodeDto[]> => {
        setLoadingChildren((prev) => new Set(prev).add(nodeKey));
        try {
          const children = await apiService.getNodeChildren(nodeKey, locale);
          setLoadedChildren((prev) => new Map(prev).set(nodeKey, children));
          return children;
        } catch (err) {
          console.error(`Failed to load children for node ${nodeKey}:`, err);
          return [];
        } finally {
          setLoadingChildren((prev) => {
            const next = new Set(prev);
            next.delete(nodeKey);
            return next;
          });
          inFlightChildrenFetchesRef.current.delete(nodeKey);
        }
      })();

      inFlightChildrenFetchesRef.current.set(nodeKey, fetchPromise);
      const result = await fetchPromise;
      inFlightChildrenFetchesRef.current.delete(nodeKey);
      return result;
    },
    [loadedChildren, locale]
  );

  // Auto-load children for genericPageFolder nodes that are hidden (showInPartnerPortal = false)
  // Visible folders (showInPartnerPortal = true) will load children on user interaction
  useEffect(() => {
    const loadGenericPageFolderChildren = async (nodes: INavigationNodeDto[]): Promise<void> => {
      for (const node of nodes) {
        if (
          node.documentType === "genericPageFolder" &&
          !node.showInPartnerPortal &&
          node.hasChildren &&
          !loadedChildren.has(node.key) &&
          !loadingChildren.has(node.key)
        ) {
          setLoadingChildren((prev) => new Set(prev).add(node.key));
          try {
            const children = await apiService.getNodeChildren(node.key, locale);
            setLoadedChildren((prev) => new Map(prev).set(node.key, children));
            // Recursively load nested genericPageFolder children
            void loadGenericPageFolderChildren(children);
          } catch (err) {
            console.error(`Failed to load children for node ${node.key}:`, err);
          } finally {
            setLoadingChildren((prev) => {
              const next = new Set(prev);
              next.delete(node.key);
              return next;
            });
          }
        }
      }
    };

    if (rootNodes.length > 0) {
      void loadGenericPageFolderChildren(rootNodes);
    }
  }, [rootNodes, locale, loadedChildren, loadingChildren]);

  // Also load genericPageFolder children when new children are loaded (only for hidden folders)
  useEffect(() => {
    const loadNestedGenericPageFolders = async (): Promise<void> => {
      for (const [, children] of loadedChildren) {
        for (const child of children) {
          if (
            child.documentType === "genericPageFolder" &&
            !child.showInPartnerPortal &&
            child.hasChildren &&
            !loadedChildren.has(child.key) &&
            !loadingChildren.has(child.key)
          ) {
            setLoadingChildren((prev) => new Set(prev).add(child.key));
            try {
              const grandChildren = await apiService.getNodeChildren(child.key, locale);
              setLoadedChildren((prev) => new Map(prev).set(child.key, grandChildren));
            } catch (err) {
              console.error(`Failed to load children for node ${child.key}:`, err);
            } finally {
              setLoadingChildren((prev) => {
                const next = new Set(prev);
                next.delete(child.key);
                return next;
              });
            }
          }
        }
      }
    };

    void loadNestedGenericPageFolders();
  }, [loadedChildren, loadingChildren, locale]);

  // Search navigation nodes via API
  const handleSearch = useCallback(
    async (searchTerm: string): Promise<void> => {
      searchAbortControllerRef.current?.abort();

      if (!searchTerm || searchTerm.length < 2) {
        setSearchResults([]);
        setIsSearching(false);
        return;
      }

      const abortController = new AbortController();
      searchAbortControllerRef.current = abortController;
      setIsSearching(true);
      try {
        const results = await apiService.searchNodes(searchTerm, locale, abortController.signal);
        if (searchAbortControllerRef.current === abortController) {
          setSearchResults(results);
        }
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") {
          return;
        }

        console.error("Failed to search:", err);
        if (searchAbortControllerRef.current === abortController) {
          setSearchResults([]);
        }
      } finally {
        if (searchAbortControllerRef.current === abortController) {
          searchAbortControllerRef.current = null;
          setIsSearching(false);
        }
      }
    },
    [locale]
  );

  // Clear search and show root nodes again
  const clearSearch = useCallback((): void => {
    searchAbortControllerRef.current?.abort();
    searchAbortControllerRef.current = null;
    setSearchResults([]);
    setIsSearching(false);
  }, []);

  useEffect(() => {
    return (): void => searchAbortControllerRef.current?.abort();
  }, []);

  // Find page title by path (for browser tab title)
  const findPageTitle = useCallback(
    (targetPath: string): string => {
      const normalizedTargetPath = normalizePathForCompare(targetPath);

      const search = (nodes: INavigationNodeDto[]): string | null => {
        for (const node of nodes) {
          const normalizedNodePath = getNormalizedPathFromUrl(node.url);
          if (normalizedNodePath && normalizedNodePath === normalizedTargetPath) {
            return node.title;
          }
          const children = loadedChildren.get(node.key);
          if (children) {
            const found = search(children);
            if (found) {
              return found;
            }
          }
        }
        return null;
      };

      return search(rootNodes) || "";
    },
    [rootNodes, loadedChildren, getNormalizedPathFromUrl]
  );

  const ensureExpandedToPath = useCallback(
    async (targetPath: string): Promise<void> => {
      if (!targetPath || rootNodes.length === 0) {
        return;
      }

      const normalizedTargetPath = normalizePathForCompare(targetPath);

      // Skip if already expanded — prevents re-opening folders the user collapsed.
      if (lastAutoExpandedPathRef.current === normalizedTargetPath) {
        return;
      }

      // Track generation to discard stale async results when user navigates quickly.
      const generation = ++expansionGenerationRef.current;

      const findExpansionKeys = async (
        nodes: INavigationNodeDto[],
        desiredPath: string
      ): Promise<string[] | null> => {
        for (const node of nodes) {
          const normalizedNodePath = getNormalizedPathFromUrl(node.url);

          if (normalizedNodePath && normalizedNodePath === desiredPath) {
            return [];
          }

          // Optimization: if this node has a URL and the target isn't under it, skip traversing.
          if (
            normalizedNodePath &&
            desiredPath !== normalizedNodePath &&
            !desiredPath.startsWith(`${normalizedNodePath}/`)
          ) {
            continue;
          }

          if (!node.hasChildren) {
            continue;
          }

          const children = await getOrFetchChildren(node.key);
          const childMatch = await findExpansionKeys(children, desiredPath);
          if (childMatch) {
            return [node.key, ...childMatch];
          }
        }

        return null;
      };

      const keysToExpand = await findExpansionKeys(rootNodes, normalizedTargetPath);
      if (!keysToExpand || keysToExpand.length === 0) {
        return;
      }

      // A newer call started while we were loading — discard this stale result.
      if (expansionGenerationRef.current !== generation) {
        return;
      }

      lastAutoExpandedPathRef.current = normalizedTargetPath;

      setExpandedItems((prev) => {
        const next = new Set(prev);
        for (const key of keysToExpand) {
          next.add(key);
        }
        return next;
      });
    },
    [rootNodes, getOrFetchChildren, getNormalizedPathFromUrl]
  );

  return {
    rootNodes,
    expandedItems,
    loadedChildren,
    loadingChildren,
    isLoading,
    error,
    searchResults,
    isSearching,
    handleToggle,
    handleLoadChildren,
    handleSearch,
    clearSearch,
    findPageTitle,
    ensureExpandedToPath,
  };
}
